import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";
import SupervisionWorkQueueApi from "../../../../../infrastructure/api/supervisionWorkQueue";
import WorkflowV2SiteVisitDetailsPanel from "../../../../components/request/WorkflowV2SiteVisitDetailsPanel";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import {
  buildInitialSiteVisitAssignmentPayload,
  clearAssigneeOnPerformerTypeChange,
  formatContractorLookupLabel,
  getAssigneeFieldLabel,
  PERFORMER_TYPE_OPTIONS,
  validateInitialSiteVisitForm,
} from "../../shared/projectWorkspace/loanMehrsanStage/initialSiteVisitAssignmentUtils";

const PAGE_TITLE = "کارتابل نظارت";
const OPERATIONS_SOURCE = "SolarentOperator";

const EMPTY_ASSIGN_DIALOG = {
  open: false,
  row: null,
  performerType: "",
  assigneeId: "",
  note: "",
};

const EMPTY_VISIT_DIALOG = {
  open: false,
  row: null,
};

export const QUEUE_TABS = [
  { key: "awaitingAssign", label: "ارجاع بازدید", countField: "awaitingAssignCount" },
  { key: "current", label: "جاری (کارتابل ناظر)", countField: "currentCount" },
  { key: "rejected", label: "رد شده", countField: "rejectedCount" },
  { key: "approved", label: "تکمیل شده", countField: "approvedCount" },
];

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

export const resolveSupervisionQueueStepDisplay = (row) => {
  if (!row) return "—";

  const queueTab = row.queueTab;
  const assignmentStatusCode = row.assignmentStatusCode;

  if (queueTab === "approved" || assignmentStatusCode === "completed") {
    return "بازدید تکمیل شده";
  }
  if (queueTab === "rejected") return "رد بازدید / نظارت";
  if (queueTab === "awaitingAssign") return "در انتظار ارجاع بازدید";
  if (assignmentStatusCode === "accepted" || assignmentStatusCode === "assigned") {
    return assignmentStatusCode === "accepted"
      ? "تکمیل فرم بازدید توسط ناظر"
      : "تعیین زمان بازدید توسط ناظر";
  }
  if (assignmentStatusCode === "pendingAccept") {
    return "تعیین زمان بازدید توسط ناظر";
  }

  return row.requestStepTitle || "—";
};

export const resolveSupervisionQueueAssigneeDisplay = (row) => {
  const name = row?.assigneeDisplayName || row?.supervisorFullName || "";
  if (!name) return "بدون ارجاع";

  const party = String(row?.assignedPartyType || "").toLowerCase();
  if (party === "contractor") return `${name} (پیمانکار)`;
  return name;
};

export const normalizeQueueRow = (raw) => ({
  requestInfoId: pick(raw, "requestInfoId", "RequestInfoId"),
  requestTrackingCode: pick(raw, "requestTrackingCode", "RequestTrackingCode"),
  applicantFullName: pick(raw, "applicantFullName", "ApplicantFullName") || "—",
  applicantMobile: pick(raw, "applicantMobile", "ApplicantMobile") || "",
  plantCapacityKw: pick(raw, "plantCapacityKw", "PlantCapacityKw"),
  requestStepTitle: pick(raw, "requestStepTitle", "RequestStepTitle") || "",
  distributionCompanyId: pick(raw, "distributionCompanyId", "DistributionCompanyId"),
  distributionCompanyName: pick(raw, "distributionCompanyName", "DistributionCompanyName") || "",
  needsLoan: pick(raw, "needsLoan", "NeedsLoan") ?? false,
  queueTab: pick(raw, "queueTab", "QueueTab") || "awaitingAssign",
  assignmentId: pick(raw, "assignmentId", "AssignmentId"),
  assignmentStatusTitle: pick(raw, "assignmentStatusTitle", "AssignmentStatusTitle") || "",
  assignmentStatusCode: pick(raw, "assignmentStatusCode", "AssignmentStatusCode") || "",
  supervisorInfoId: pick(raw, "supervisorInfoId", "SupervisorInfoId"),
  supervisorFullName: pick(raw, "supervisorFullName", "SupervisorFullName") || "",
  assigneeDisplayName: pick(raw, "assigneeDisplayName", "AssigneeDisplayName") || "",
  assignedPartyType: pick(raw, "assignedPartyType", "AssignedPartyType"),
  assignedToUserId: pick(raw, "assignedToUserId", "AssignedToUserId"),
  assignedAt: pick(raw, "assignedAt", "AssignedAt"),
  rejectReason: pick(raw, "rejectReason", "RejectReason") || "",
  enteredQueueAt: pick(raw, "enteredQueueAt", "EnteredQueueAt"),
  lastActionAt: pick(raw, "lastActionAt", "LastActionAt"),
  openDays: pick(raw, "openDays", "OpenDays") ?? 0,
});

const SupervisionWorkQueuePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    awaitingAssignCount: 0,
    currentCount: 0,
    rejectedCount: 0,
    approvedCount: 0,
  });
  const [activeTab, setActiveTab] = useState("awaitingAssign");
  const [supervisors, setSupervisors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [assignDialog, setAssignDialog] = useState(EMPTY_ASSIGN_DIALOG);
  const [performers, setPerformers] = useState([]);
  const [loadingPerformers, setLoadingPerformers] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignFormError, setAssignFormError] = useState("");
  const [visitDialog, setVisitDialog] = useState(EMPTY_VISIT_DIALOG);
  const [busyRequestId, setBusyRequestId] = useState(null);

  const assigneeFieldLabel = getAssigneeFieldLabel(assignDialog.performerType);

  const loadFilterData = useCallback(async () => {
    try {
      const res = await SupervisionWorkQueueApi.getFilterData(OPERATIONS_SOURCE);
      const data = res?.data ?? res;
      const list = data?.supervisors ?? data?.Supervisors ?? [];
      setSupervisors(Array.isArray(list) ? list : []);
    } catch {
      setSupervisors([]);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (appliedSearch.trim()) payload.searchText = appliedSearch.trim();
      if (supervisorFilter !== "") payload.supervisorInfoId = Number(supervisorFilter);

      const res = await SupervisionWorkQueueApi.search(OPERATIONS_SOURCE, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت کارتابل نظارت");
        setRows([]);
        setCounts({
          awaitingAssignCount: 0,
          currentCount: 0,
          rejectedCount: 0,
          approvedCount: 0,
        });
        return;
      }

      const data = res?.data ?? res;
      const list = data?.rows ?? data?.Rows ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeQueueRow) : []);
      setCounts({
        awaitingAssignCount: data?.awaitingAssignCount ?? data?.AwaitingAssignCount ?? 0,
        currentCount: data?.currentCount ?? data?.CurrentCount ?? 0,
        rejectedCount: data?.rejectedCount ?? data?.RejectedCount ?? 0,
        approvedCount: data?.approvedCount ?? data?.ApprovedCount ?? 0,
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت کارتابل نظارت");
      setRows([]);
      setCounts({
        awaitingAssignCount: 0,
        currentCount: 0,
        rejectedCount: 0,
        approvedCount: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, supervisorFilter]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadPerformers = useCallback(async (performerType) => {
    if (!performerType) {
      setPerformers([]);
      return;
    }
    setLoadingPerformers(true);
    try {
      const res = await ProjectWorkflowFoundationApi.getInitialSiteVisitPerformerLookups(
        OPERATIONS_SOURCE,
        performerType
      );
      setPerformers(res?.data?.performers ?? res?.data?.Performers ?? []);
    } catch {
      setPerformers([]);
    } finally {
      setLoadingPerformers(false);
    }
  }, []);

  useEffect(() => {
    if (!assignDialog.open || !assignDialog.performerType) {
      setPerformers([]);
      return;
    }
    loadPerformers(assignDialog.performerType);
  }, [assignDialog.open, assignDialog.performerType, loadPerformers]);

  const openAssignDialog = useCallback((row) => {
    setAssignFormError("");
    setAssignDialog({
      open: true,
      row,
      performerType: "",
      assigneeId: "",
      note: "",
    });
  }, []);

  const closeAssignDialog = useCallback(() => {
    setAssignDialog(EMPTY_ASSIGN_DIALOG);
    setPerformers([]);
    setAssignFormError("");
    setAssignSaving(false);
  }, []);

  const handleAssignConfirm = useCallback(async () => {
    const { row, performerType, assigneeId, note } = assignDialog;
    const validationError = validateInitialSiteVisitForm({ performerType, assigneeId });
    if (validationError) {
      setAssignFormError(validationError);
      toast.error(validationError);
      return;
    }
    if (!row?.requestInfoId) return;

    setAssignSaving(true);
    setBusyRequestId(row.requestInfoId);
    try {
      const res = await ProjectWorkflowFoundationApi.upsertInitialSiteVisitAssignment(
        OPERATIONS_SOURCE,
        buildInitialSiteVisitAssignmentPayload({
          requestId: row.requestInfoId,
          performerType,
          assigneeId,
          note,
        })
      );
      if (!res.ok) {
        toast.error(res.message || "خطا در ثبت ارجاع بازدید");
        return;
      }
      toast.success(res.message || "ارجاع بازدید ثبت شد");
      closeAssignDialog();
      setActiveTab("current");
      await loadList();
    } catch (e) {
      toast.error(e?.message || "خطا در ثبت ارجاع بازدید");
    } finally {
      setAssignSaving(false);
      setBusyRequestId(null);
    }
  }, [assignDialog, closeAssignDialog, loadList]);

  const tabRows = useMemo(
    () => rows.filter((r) => r.queueTab === activeTab),
    [rows, activeTab]
  );

  const STATUS_CHIP_COLORS = {
    pendingAccept: "warning",
    assigned: "info",
    accepted: "success",
    rejected: "error",
    completed: "info",
  };

  const tableRows = useMemo(
    () =>
      tabRows.map((row, index) => {
        const assigneeLabel = resolveSupervisionQueueAssigneeDisplay(row);
        return {
          ...row,
          row_index: index + 1,
          capacityDisplay: row.plantCapacityKw ? `${row.plantCapacityKw} kW` : "—",
          stepDisplay: resolveSupervisionQueueStepDisplay(row),
          distributionCompanyDisplay: row.distributionCompanyName || "—",
          supervisorDisplay: assigneeLabel,
          supervisorColor:
            assigneeLabel !== "بدون ارجاع" ? "info" : "default",
          assignmentStatusDisplay: row.assignmentStatusTitle || "—",
          assignmentStatusColor:
            STATUS_CHIP_COLORS[row.assignmentStatusCode] || "default",
          lastActionDisplay: row.lastActionAt ? toJalaliFullDate(row.lastActionAt) : "—",
          openDaysDisplay: `${row.openDays} روز`,
          rejectReasonDisplay: row.rejectReason || "—",
        };
      }),
    [tabRows]
  );

  const headCells = useMemo(() => {
    const base = [
      { id: "row_index", label: "ردیف" },
      { id: "requestTrackingCode", label: "کد پیگیری" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "capacityDisplay", label: "ظرفیت" },
      { id: "stepDisplay", label: "مرحله" },
      { id: "distributionCompanyDisplay", label: "شرکت توزیع" },
      {
        id: "supervisorDisplay",
        label: activeTab === "current" ? "ناظر / مجری ارجاع‌شده" : "مجری بازدید",
        type: "chip",
        model: { colorField: "supervisorColor" },
      },
    ];

    if (activeTab === "current") {
      base.push({
        id: "assignmentStatusDisplay",
        label: "وضعیت ارجاع",
        type: "chip",
        model: { colorField: "assignmentStatusColor" },
      });
    }

    base.push(
      { id: "lastActionDisplay", label: "آخرین اقدام" },
      { id: "openDaysDisplay", label: "روزهای باز" }
    );

    if (activeTab === "rejected") {
      base.splice(base.length - 2, 0, {
        id: "rejectReasonDisplay",
        label: "دلیل رد",
      });
    }
    return base;
  }, [activeTab]);

  const tableActions = useMemo(
    () => [
      {
        icon: <OpenInNewIcon fontSize="small" />,
        onClick: (row) =>
          navigate(`/SLN/SolarentOperator/ProjectWorkspace/${row.requestInfoId}?view=all`),
        color: "primary",
        tooltip: "باز کردن پرونده",
      },
      {
        icon: <VisibilityIcon fontSize="small" />,
        onClick: (row) => setVisitDialog({ open: true, row }),
        color: "secondary",
        tooltip: "مشاهده فرم بازدید",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          (row.queueTab !== "approved" && row.queueTab !== "current"),
      },
      {
        icon: <PersonAddAlt1Icon fontSize="small" />,
        onClick: (row) => openAssignDialog(row),
        color: "info",
        tooltip: "ارجاع بازدید (ناظر / پیمانکار)",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          row.queueTab === "approved" ||
          row.queueTab === "current",
      },
    ],
    [busyRequestId, navigate, openAssignDialog]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="ارجاع بازدید: تأیید اولیه‌شده بدون ارجاع — جاری: پیگیری بازدید توسط ناظر/پیمانکار — تکمیل‌شده: بازدید انجام‌شده"
            action={
              <Button
                startIcon={<SyncIcon />}
                onClick={loadList}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                بروزرسانی
              </Button>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <TextField
                size="small"
                placeholder="جستجو نام / موبایل / کد / ناظر / شرکت توزیع"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(searchText)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>ناظر</InputLabel>
                <Select
                  label="ناظر"
                  value={supervisorFilter}
                  onChange={(e) => setSupervisorFilter(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {supervisors.map((s) => (
                    <MenuItem key={s.id ?? s.Id} value={s.id ?? s.Id}>
                      {s.title ?? s.Title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={() => setAppliedSearch(searchText)}>
                اعمال فیلتر
              </Button>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {QUEUE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Badge
                      badgeContent={counts[tab.countField] || 0}
                      color={
                        tab.key === "rejected"
                          ? "error"
                          : tab.key === "approved"
                            ? "success"
                            : "primary"
                      }
                      sx={{ "& .MuiBadge-badge": { right: -12 } }}
                    >
                      <Typography component="span" sx={{ pr: 1.5 }}>
                        {tab.label}
                      </Typography>
                    </Badge>
                  }
                />
              ))}
            </Tabs>

            <EnhancedTable
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              loading={loading}
              showCheckbox={false}
              dense
              defaultSort={{ orderBy: "requestTrackingCode", order: "desc" }}
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={assignDialog.open}
        onClose={closeAssignDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>ارجاع بازدید اولیه</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
            {assignDialog.row?.requestTrackingCode ? (
              <Typography variant="body2" color="text.secondary">
                کد پیگیری: {assignDialog.row.requestTrackingCode}
                {assignDialog.row.applicantFullName
                  ? ` — ${assignDialog.row.applicantFullName}`
                  : ""}
              </Typography>
            ) : null}

            <FormControl
              fullWidth
              size="small"
              error={Boolean(assignFormError) && !assignDialog.performerType}
            >
              <InputLabel id="supervision-queue-performer-type-label">
                مجری بازدید اولیه
              </InputLabel>
              <Select
                labelId="supervision-queue-performer-type-label"
                value={assignDialog.performerType}
                label="مجری بازدید اولیه"
                onChange={(e) => {
                  const nextPerformerType = e.target.value;
                  setAssignDialog((s) => ({
                    ...s,
                    performerType: nextPerformerType,
                    assigneeId: clearAssigneeOnPerformerTypeChange({
                      previousPerformerType: s.performerType,
                      nextPerformerType,
                      assigneeId: s.assigneeId,
                    }),
                  }));
                  setAssignFormError("");
                }}
              >
                {PERFORMER_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!assignDialog.performerType || loadingPerformers}
              error={
                Boolean(assignFormError) &&
                Boolean(assignDialog.performerType) &&
                !assignDialog.assigneeId
              }
            >
              <InputLabel id="supervision-queue-assignee-label">
                {assigneeFieldLabel}
              </InputLabel>
              <Select
                labelId="supervision-queue-assignee-label"
                value={assignDialog.assigneeId}
                label={assigneeFieldLabel}
                onChange={(e) => {
                  setAssignDialog((s) => ({ ...s, assigneeId: e.target.value }));
                  setAssignFormError("");
                }}
              >
                {(performers || []).map((item) => (
                  <MenuItem
                    key={item.id ?? item.Id}
                    value={String(item.id ?? item.Id)}
                  >
                    {formatContractorLookupLabel(item)}
                  </MenuItem>
                ))}
              </Select>
              {loadingPerformers ? (
                <FormHelperText>در حال بارگذاری فهرست مجریان...</FormHelperText>
              ) : null}
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="توضیحات (اختیاری)"
              multiline
              minRows={2}
              value={assignDialog.note}
              onChange={(e) =>
                setAssignDialog((s) => ({ ...s, note: e.target.value }))
              }
            />

            {assignFormError ? (
              <Typography variant="caption" color="error">
                {assignFormError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} disabled={assignSaving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            disabled={
              assignSaving ||
              !assignDialog.performerType ||
              !assignDialog.assigneeId
            }
            onClick={handleAssignConfirm}
            startIcon={
              assignSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            ثبت ارجاع بازدید
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={visitDialog.open}
        onClose={() => setVisitDialog(EMPTY_VISIT_DIALOG)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle>
          فرم اطلاعات بازدید
          {visitDialog.row?.requestTrackingCode
            ? ` — ${visitDialog.row.requestTrackingCode}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {visitDialog.row?.applicantFullName ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {visitDialog.row.applicantFullName}
              {visitDialog.row.assigneeDisplayName ||
              visitDialog.row.supervisorFullName
                ? ` · مجری: ${
                    visitDialog.row.assigneeDisplayName ||
                    visitDialog.row.supervisorFullName
                  }`
                : ""}
            </Typography>
          ) : null}
          {visitDialog.open && visitDialog.row?.requestInfoId ? (
            <WorkflowV2SiteVisitDetailsPanel
              key={visitDialog.row.requestInfoId}
              requestId={visitDialog.row.requestInfoId}
              source={OPERATIONS_SOURCE}
              showTrigger={false}
              autoLoad
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitDialog(EMPTY_VISIT_DIALOG)}>بستن</Button>
          {visitDialog.row?.requestInfoId ? (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                const id = visitDialog.row.requestInfoId;
                setVisitDialog(EMPTY_VISIT_DIALOG);
                navigate(`/SLN/SolarentOperator/ProjectWorkspace/${id}?view=visit`);
              }}
            >
              باز کردن در پرونده
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SupervisionWorkQueuePage;
